import { PublicClientApplication } from '@azure/msal-browser';
import { Acl } from '../../shared/auth/acl';
import { AuthRoles } from '../../shared/interfaces/acl/acl-i';
import { ICurrentUser, ILoginconfig } from '../../shared/interfaces/user/user';
import { getClLogger } from '../../shared/util/clLogger';
import { prettyDateNoTime, stringToDate } from '../../shared/util/dateUtil';
import { FeatureFlags } from '../../shared/util/featureFlag';
import { emailMatchesDomain } from '../../shared/util/stringUtil';
import { setupAdminDMs } from '../apps/admin/adminDmWidget';
import {
    attr, create, getByClass, getByClassFirst, getById, hide, onClick,
    onDomLoaded, onEvent, onInput, onKeyDown, qs, qsa, removeEvent, show, SPACE_KEYCODE, tooltip,
} from '../lib/util/html';
import { ajaxFormOnSubmitWithControl, newEvent } from './ajax';
import { rootGetLoginConfig } from './api/root';
import { servicesPostPageView } from './api/services';
import { Autocomplete } from './autocomplete/autocomplete';
import { isFlagEnabledForView } from './featureFlag';
import { UserFeature } from './interfaces/analytics';
import { IGtagDetails, ModalSizeEnum } from './interfaces/static';
import { setupDM } from './message/dm';
import { ifExistsOnClickTrackAnalyticsEvent, trackAnalyticsEvent } from './util/analytics';
import { getConfirmModal } from './util/confirm';
import { ClModal } from './util/modalWidget';
import { STORAGE_INFO, clearOldCache, fetchCacheKey, removeCacheKey, setCacheKey } from './util/storageUtil';
import { LoginWidget } from './widgets/login/loginWidget';
import { NavWidget } from './widgets/nav/navWidget';
import { NotifyWidget } from './widgets/notify/notifyWidget';
import { TimeMachine } from './widgets/timeMachine/timeMachine';
const genericErrorTemplate = require('../lib/util/genericError.pug');
const loginTmpl = require('../lib/util/login.pug');
const eulaTmpl = require('./userHelp/eula.pug');
const embedVideoTemplate = require('./userHelp/videoframe.pug');

const clLogger = getClLogger(__filename);
const loginWidget = new LoginWidget('userList');
const navWidget = new NavWidget('navDiv');
const notifyWidget = new NotifyWidget('notificationList');
const PAGE_MESSAGES_ID = 'pageMessages';
type LoginListener = (acl: Acl) => void | Promise<void>;
const loginListeners: LoginListener[] = [];
let readyLoginAcl: Acl = null;
let loginFailureReported = false;
export const TIME_MACHINE_LOADED_EVENT = 'time-machine-modal.loaded';
export const timeMachine = new TimeMachine(() => location.reload());
let stemWidget = null;
let parseWidget = null;
let parseModalGeneration = 0;
let metadataMonitorWidget = null;
let daasSsoWidget = null;
let unauthorizedUserModal: ClModal = null;
let timeMachineModal: ClModal = null;
let stemModal: ClModal = null;
let parseModal: ClModal = null;
let metaMonitorModal: ClModal = null;
let daasSsoModal: ClModal = null;
let eulaModal: ClModal = null;
let helpVidsModal: ClModal = null;
let genericErrorModal: ClModal = null;
let enableChatSupportModal: ClModal = null;
// See webpack.common.js on using define plugin to fill this using webpack
declare let GOOGLEANALYTICS: string;
declare let GOOGLEANALYTICS_V4: string;

const dlWindow: Window & { dataLayer?: IArguments[]} = window;
// Create or get a handle to a data layer shared with Google Analytics
const gdata = dlWindow.dataLayer || [];
dlWindow.dataLayer = gdata;

// Used to broadcast a new tag/event with GAs
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function gtag(_key, _val, _details?: IGtagDetails) {
    // eslint-disable-next-line prefer-rest-params
    gdata.push(arguments);
}

export function addLoginListener(func: LoginListener) {
    // User data can arrive before navigation, chat, and the queued listeners are ready.
    if (readyLoginAcl) {
        runLoginListener(func, readyLoginAcl);
    }
    else {
        loginListeners.push(func);
    }
}

function runLoginListener(func: LoginListener, acl: Acl) {
    try {
        Promise.resolve(func(acl)).catch(reportLoginListenerFailure);
    }
    catch (err) {
        reportLoginListenerFailure(err);
    }
}

// Pages that handle their own startup errors never reach this; it is the safety net for the rest, shown once per page.
function reportLoginListenerFailure(err: unknown) {
    clLogger.error('Login listener failed', err);
    if (!loginFailureReported) {
        loginFailureReported = true;
        showPageMessage('Part of this page failed to load. Please reload the page to try again.');
    }
}

// Surfaces a failure anywhere in page initialization (login config, profile, navigation, chat) instead of leaving a silently broken page.
function guardPageInit(initialize: () => Promise<void>): Promise<void> {
    return initialize().catch((err) => {
        clLogger.error('Page initialization failed', err);
        showPageMessage('The page did not finish loading. Please reload the page to try again.');
    });
}

export function getUser(): ICurrentUser {
    return loginWidget.getUser();
}

export function getAcl(): Acl | null {
    return loginWidget.getAcl();
}

function clearCache() {
    setTimeout(() => {
        clearOldCache().catch((err) => clLogger.error('The cache sweep failed', err));
        clearCache();
    }, 60000);
}

/*
 * This is an important entry point for all sort of setup and authentication.
 * It should NEVER be called by a library, only by applications that are webpack entry modules,
 * and only once at the top of those pages.
 */

export function init(skipLogin?: boolean, requiredRoles?: AuthRoles[][], alternativeUrl?: string, timeMachinePage?: boolean, skipFreeUser?: boolean) {
    // This initializes UGA
    gtag('js', new Date());
    onDomLoaded(() => guardPageInit(async () => {
        clearCache();
        clLogger.debug('Loaded...');
        tooltip(document.body, null, { selector: '.trendBar[data-bs-toggle="tooltip"]' });
        tooltip(document.body, null, { selector: '.trendBar[data-bs-toggle="tooltip"]' });
        tooltip(qs('.cl-content'), null, { selector: '.helpBadge[data-bs-toggle="tooltip"]' });

        // Initialize all modals so they are defined
        unauthorizedUserModal = new ClModal(ModalSizeEnum.Normal, undefined, false, () => {
            const loginUserFill = fetchCacheKey<string>(STORAGE_INFO.clTempUser.scope, STORAGE_INFO.clTempUser.key) ||
                fetchCacheKey<string>(STORAGE_INFO.clLastUser.scope, STORAGE_INFO.clLastUser.key);
            removeCacheKey(STORAGE_INFO.clTempUser.scope, STORAGE_INFO.clTempUser.key);
            const configuredSsoDomains = externalLoginConfig?.ssoDomains ?? [];
            return loginTmpl({ loginUserFill, pathname: window.location.pathname, ssoMode: emailMatchesDomain(loginUserFill, configuredSsoDomains) });
        }, (bodyContainer) => {
            const formLogin = getById<HTMLFormElement>('formLogin');
            const username = getByClassFirst<HTMLInputElement>('cl-username', formLogin);
            const pass = getByClassFirst<HTMLInputElement>('cl-pass', formLogin);
            const user = getByClassFirst<HTMLInputElement>('cl-user', formLogin);
            const accessToken = getByClassFirst<HTMLInputElement>('cl-accessToken', formLogin);
            const errDiv = getByClassFirst('text-danger', formLogin);
            const ssoDomains = externalLoginConfig?.ssoDomains ?? [];
            // Deals with better experience when logging in with SSO
            let ssoMode = showPass(false, pass, user, accessToken, username, ssoDomains);
            onInput(username, () => {
                ssoMode = showPass(ssoMode, pass, user, accessToken, username, ssoDomains);
            });
            ajaxFormOnSubmitWithControl(formLogin, async () => {
                const cleanUser = username.value.trim();
                if (emailMatchesDomain(cleanUser, ssoDomains) && msalSession) {
                    // msalSession.loginPopup seems to be messing with the icon, if I reload the icon after it doesn't work, if I comment msalSession.loginPopup
                    // I don't need to refresh the icon (?)
                    updateImage();
                    try {
                        const result = await msalSession.loginPopup({
                            scopes: externalLoginConfig?.scopes,
                        });
                        // Normalize the msal-browser result to the shape the /login route already
                        // expects (msal v1 used Account.userName and a nested idToken.rawIdToken),
                        // so the server contract is unchanged.
                        user.value = JSON.stringify({
                            userName: result.account.username,
                            // AccountInfo.name is optional; fall back to the username so the server
                            // (loginSSOUser splits name into first/last) never receives undefined.
                            name: result.account.name || result.account.username,
                        });
                        accessToken.value = JSON.stringify({
                            idToken: { rawIdToken: result.idToken },
                        });
                    }
                    catch (err) {
                        clLogger.error(err);
                        errDiv.firstElementChild.innerHTML = err.message;
                        return false;
                    }
                }
                return true;
            }, (err, result) => {
                if (err) {
                    clLogger.error('Error after submitting form...');
                    clLogger.error(JSON.stringify(err));
                }
                else {
                    if (result.message === 'Success') {
                        errDiv.firstElementChild.innerHTML = '';
                        unauthorizedUserModal.close();
                        const cleanUser = username.value.trim();
                        if (emailMatchesDomain(cleanUser, ssoDomains)) {
                            setCacheKey(STORAGE_INFO.clLastUser.scope, STORAGE_INFO.clLastUser.key, cleanUser);
                        }
                        if (result.redirect) {
                            window.location.href = result.redirect;
                        }
                        else {
                            document.dispatchEvent(newEvent('AuthorizedUser'));
                        }
                    }
                    else {
                        errDiv.firstElementChild.innerHTML = result.message;
                        if (document.activeElement instanceof HTMLInputElement) {
                            const ae = document.activeElement as HTMLInputElement;
                            ae.select();
                        }
                    }
                }
            });
            setTimeout(() => {
                qs('input', bodyContainer).focus();
            }, 500);
        });

        timeMachineModal = new ClModal(ModalSizeEnum.Normal, 'Time Machine', true, () => {
            return '';
        }, (bodyContainer) => {
            timeMachine.mount(bodyContainer, timeMachineModal);
            // Send a message about load completion
            // If this works as a simple and scalable mechanism for load notifications, we can help further build it out
            //  by enumerating available message types
            const html = qs('html');
            if (html) {
                html.dispatchEvent(new Event(TIME_MACHINE_LOADED_EVENT));
            }
        });

        stemModal = new ClModal(ModalSizeEnum.Large, 'Manage Stemming', true, () => {
            return '<div id="stemDiv"></div>';
        }, async () => {
            if (stemWidget === null) {
                const sw = await import(/* webpackChunkName: "stem" */ './widgets/stem/stemWidget');
                stemWidget = new sw.StemWidget('stemDiv');
            }
            await stemWidget.render();
            setTimeout(() => {
                qs('input', getById('stemDiv')).focus();
            }, 500);
        });

        parseModal = new ClModal(ModalSizeEnum.Fullscreen, 'Parse', true, () => {
            return '<div id="parseWidgetDiv"></div>';
        }, async (_bodyContainer, params) => {
            if (params.generation !== parseModalGeneration) {
                return;
            }
            if (parseWidget === null) {
                const pw = await import(/* webpackChunkName: "parseWorkspace" */ './widgets/parse/parseWidget');
                parseWidget = new pw.ParseWidget('parseWidgetDiv');
            }
            if (params.generation !== parseModalGeneration) {
                return;
            }
            await parseWidget.render();
        }, () => {
            parseModalGeneration++;
            parseWidget?.leave();
        });

        metaMonitorModal = new ClModal(ModalSizeEnum.Large, 'Monitor', true, () => {
            return '<div id="metaMonitorDiv"></div>';
        }, async () => {
            if (metadataMonitorWidget === null) {
                const sw = await import(/* webpackChunkName: "metadataMonitor" */ './widgets/metadataMonitor/metadataMonitorWidget');
                metadataMonitorWidget = new sw.MetadataMonitorWidget('metaMonitorDiv');
            }
            await metadataMonitorWidget.render();
        }, async () => {
            await metadataMonitorWidget.leave();
        });

        daasSsoModal = new ClModal(ModalSizeEnum.Large, 'Data-as-a-Service', true, () => {
            return '<div id="daasSsoWidgetDiv"></div>';
        }, async () => {
            if (daasSsoWidget === null) {
                const ds = await import(/* webpackChunkName: "daasSso" */ './widgets/daasSso/daasSsoWidget');
                daasSsoWidget = new ds.DaasSsoWidget('daasSsoWidgetDiv');
            }
            await daasSsoWidget.render();
        }, async () => {
            await daasSsoWidget.leave();
        });

        eulaModal = new ClModal(ModalSizeEnum.Large, 'End-User License Agreement ("Agreement")', true, () => {
            return eulaTmpl();
        });

        helpVidsModal = new ClModal(ModalSizeEnum.Large, '', true, () => {
            return '';
        }, (bodyContainer, params) => {
            bodyContainer.innerHTML = embedVideoTemplate({ embed: params.embed, width: 740, height: 600 });
        });

        genericErrorModal = new ClModal(ModalSizeEnum.Normal, 'Error', true, (params) => {
            return genericErrorTemplate(params);
        });

        const icons = {
            companyGroup: 'fa-building',
            industryGroup: 'fa-industry',
            location: 'fa-map-marker-alt',
        };

        // Msal has to be setup early on, this allows it to intercept login popup results
        let externalLoginConfig: ILoginconfig;
        let msalSession: PublicClientApplication;
        const contentDiv = getByClassFirst('cl-content');
        if (skipLogin !== true) {
            externalLoginConfig = await getLoginConfig();
            try {
                msalSession = new PublicClientApplication({
                    auth: {
                        clientId: externalLoginConfig.clientId,
                        authority: externalLoginConfig.authority,
                        // The popup returns to the redirect bridge page (apps/auth/redirect.pug), which relays
                        // the response to this window and closes; msal-browser 5 no longer polls the popup URL.
                        redirectUri: window.location.origin + '/auth/redirect',
                    },
                    cache: {
                        cacheLocation: 'localStorage',
                    },
                });
                // msal-browser (v3+) requires initialize() to resolve before any other API call.
                await msalSession.initialize();
            }
            catch (err) {
                // Don't let an SSO setup failure abort the rest of page init. msalSession stays
                // undefined and the login handler (guarded on msalSession) falls back to non-SSO.
                clLogger.error(err);
                msalSession = undefined;
            }
            await loginWidget.render();
            if (loginWidget.user) {
                // Associate the user with this GA session
                gtag('set', { user_id: loginWidget.user.id });
                const acl: Acl = loginWidget.getAcl();
                let notValidRoles = false;
                if (requiredRoles && requiredRoles.length) {
                    notValidRoles = true;
                    for (const r of requiredRoles) {
                        if (r && r.length && acl.hasRoles(r)) {
                            notValidRoles = false;
                        }
                    }
                }
                if (notValidRoles || (skipFreeUser && acl.hasRole(AuthRoles.FreeUser) && !acl.hasRole(AuthRoles.CLAdmin))) {
                    clLogger.debug('The user is missing one or more of the required roles', requiredRoles);
                    // Send previous urls to check for loops
                    let previousPages = '?previousPages=';
                    if (window.location.search.startsWith(previousPages)) {
                        previousPages = `${window.location.search},`;
                    }
                    previousPages += window.location.pathname;
                    if (alternativeUrl && previousPages.toLowerCase().includes(alternativeUrl.toLowerCase())) {
                        alternativeUrl = null;
                    }
                    window.location.replace(`${alternativeUrl || '/401'}${previousPages}`);
                    return;
                }
                const page = document.body.dataset.page;
                if (page) {
                    servicesPostPageView(page).catch((err) => clLogger.debug('Could not record page view', err));
                }
                await navWidget.render(acl);
                qsa('[data-bs-toggle="tooltip"]')
                    .forEach((elem: HTMLElement) => {
                        tooltip(elem);
                    });
                contentDiv.classList.remove('invisible');
                notifyWidget.render(loginWidget.user.id, acl);
                if (acl.hasRole(acl.getAuthRoles().CLAdmin)) {
                    // Hide all DM-related items to start
                    hide(getById('clientDM'));
                    hide(getById('adminDMs'));
                    hide(getById('adminDM'));
                    hide(getById('adminDmNotifs'));
                    if (getById('clientChatPage')) {
                        await setupAdminDMs(loginWidget.user.id, true);
                    }
                    else if (getById('adminDMs')) {
                        const dmsEnabled = await isFlagEnabledForView(FeatureFlags.enableChat);
                        const dmToggle = fetchCacheKey(STORAGE_INFO.offPageDMs.scope, STORAGE_INFO.offPageDMs.key);
                        if (dmsEnabled) {
                            show(getById('adminDMs'));
                        }
                        if (dmToggle === null) {
                            // If no cache history, default offPageDM support to being off
                            setCacheKey(STORAGE_INFO.offPageDMs.scope, STORAGE_INFO.offPageDMs.key, false);
                        }
                        if (dmToggle) {
                            // Only initiate when not on Client Chat page and when enabled
                            await setupAdminDMs(loginWidget.user.id, false);
                        }
                        else {
                            const dmBtnListener = () => {
                                enableChatSupportModal.open();
                            };
                            // This allows users to message you when on any page, not just the designated Client Chat page.
                            enableChatSupportModal = getConfirmModal('Enable Off-Page Chat Support?', () => `When enabled, clicking the chat bubble
                             icon on the navigation bar will open a windowed view of chat support.
                             This setting will be reset upon logout.`, async () => {
                                removeEvent(getById('adminDMs'), 'click', dmBtnListener);
                                setCacheKey(STORAGE_INFO.offPageDMs.scope, STORAGE_INFO.offPageDMs.key, true);
                                enableChatSupportModal.close();
                                await setupAdminDMs(loginWidget.user.id, false);
                            });
                            onClick(getById('adminDMs'), dmBtnListener);
                        }
                    }
                }
                else {
                    hide(getById('adminDM'));
                    await setupDM(loginWidget.user.id);
                }
                readyLoginAcl = acl;
                // Preserve registration order (Time Machine is first) and consume each listener once.
                for (const func of loginListeners.splice(0)) {
                    runLoginListener(func, acl);
                }
                const helpVids = getByClass('linkHelpVid');
                for (const vid of helpVids) {
                    const embed = attr(vid, 'data-embed');
                    onClick(vid, () => {
                        helpVidsModal.open({ embed });
                    });
                }

                const searchBar = getById<HTMLDivElement>('searchbar');
                if (searchBar) {
                    if (acl.hasRole(acl.getAuthRoles().DriversRead)) {
                        const indicatorSearch = new Autocomplete((term) => {
                            return { url: '/knowledge/indicatorSearch', searchString: term };
                        }, (sugg, index) => {
                            return {
                                auto: sugg.marker,
                                index,
                                html: true,
                                contents: `<i class="me-2 fa ${icons[sugg.type]}"></i><span>${sugg.label}</span>`,
                            };
                        }, false);
                        indicatorSearch.render(searchBar);
                        indicatorSearch.onSpecificSearch.push((selected) => {
                            trackAnalyticsEvent(UserFeature.HEADER_SEARCH_INPUT_SUBMIT);
                            location.href = '/signal/' + selected.marker;
                        });
                        indicatorSearch.onClear.push(() => {
                            trackAnalyticsEvent(UserFeature.HEADER_SEARCH_CLEARBUTTON_CLICK);
                        });
                    }
                    else {
                        searchBar.remove();
                    }
                }
            }

            // This sends the GA page view, after we know/associate the user if possible
            gtag('config', GOOGLEANALYTICS);
            gtag('config', GOOGLEANALYTICS_V4);
        }
        else {
            // No user will be associated
            gtag('config', GOOGLEANALYTICS);
            gtag('config', GOOGLEANALYTICS_V4);
        }

        onKeyDown(document, (e: Event) => {
            const searchBar = getById<HTMLDivElement>('searchbar');
            // Control space to quick search
            if (e instanceof KeyboardEvent && e.ctrlKey && e.keyCode === SPACE_KEYCODE && searchBar) {
                qs('input', searchBar).focus();
            }
        });

        const eulaFooterLink = getById('eulaFooterLink');
        if (eulaFooterLink) {
            onClick(eulaFooterLink, () => {
                eulaModal.open();
            });
        }

        if (timeMachine.isActive() && timeMachinePage) {
            const adjust = create<HTMLAnchorElement>('a', [ 'alert-link' ]);
            attr(adjust, 'role', 'button');
            onClick(adjust, runTimeMachine);
            adjust.innerText = 'adjust';
            const reset = create<HTMLAnchorElement>('a', [ 'alert-link' ]);
            attr(reset, 'role', 'button');
            onClick(reset, () => {
                timeMachine.reset();
                location.reload();
            });
            reset.innerText = 'reset';
            // Show date as is, don't convert to local date
            const shownFor = prettyDateNoTime(stringToDate(timeMachine.nowAsStr()), null, 'UTC');
            showPageMessage([ `Reports are being shown for ${shownFor}. Use the time feature to `, adjust, ' or ', reset, '.' ], { type: 'warning' });
        }
        ifExistsOnClickTrackAnalyticsEvent(getById('helpLink'), UserFeature.HEADER_HELP_MENU_CLICK);
        ifExistsOnClickTrackAnalyticsEvent(getById('sendFooterEmail'), UserFeature.FOOTER_CONTACT_BUTTON_CLICK);
        ifExistsOnClickTrackAnalyticsEvent(getById('eulaFooterLink'), UserFeature.FOOTER_TERMS_LINK_CLICK);
    }));

    onEvent(document, 'UnauthorizedUser', () => {
        unauthorizedUserModal.open();
    });

    onEvent(document, 'RequestTimeMachine', () => {
        runTimeMachine();
    });

    onEvent(document, 'RequestStemUtility', () => {
        runStemUtil();
    });

    onEvent(document, 'RequestParseUtility', () => {
        parseModal.open({ generation: ++parseModalGeneration });
    });

    onEvent(document, 'RequestMetadataMonitorUtility', () => {
        runMetadataMonitorUtil();
    });

    onEvent(document, 'RequestDaasSsoModal', () => {
        daasSsoModal.open();
    });
}

// A simple way to show a message that will overlay the screen with a dismissible modal.
export function showGenericError(message: string) {
    genericErrorModal.open({ message });
}

export type PageMessageType = 'danger' | 'warning' | 'info' | 'success';

export interface IPageMessageOptions {
    // Bootstrap contextual style; danger unless given
    type?: PageMessageType;
    // Adds a close button; Bootstrap removes the alert when it is dismissed
    dismissible?: boolean;
}

/*
 * Shows a page-level Bootstrap alert in the message area under the navigation bar (layoutMixins.pug).
 * Content can mix text with elements such as links that need click handlers. Returns the alert so the
 * caller can remove it once the condition clears.
 */
export function showPageMessage(content: string | (string | Node)[], options: IPageMessageOptions = {}): HTMLDivElement {
    let container = getById<HTMLDivElement>(PAGE_MESSAGES_ID);
    if (!container) {
        // Layouts without the slot still show the message at the top of the page
        container = create<HTMLDivElement>('div');
        container.id = PAGE_MESSAGES_ID;
        document.body.prepend(container);
    }
    const alert = create<HTMLDivElement>('div', [ 'alert', `alert-${options.type || 'danger'}`, 'rounded-0', 'mb-0', 'text-center' ]);
    attr(alert, 'role', 'alert');
    alert.append(...(Array.isArray(content) ? content : [ content ]));
    if (options.dismissible) {
        alert.classList.add('alert-dismissible');
        const close = create<HTMLButtonElement>('button', [ 'btn-close' ]);
        attr(close, 'type', 'button');
        attr(close, 'data-bs-dismiss', 'alert');
        attr(close, 'aria-label', 'Close');
        alert.append(close);
        // Bootstrap detaches the alert before firing this, so the count no longer includes it
        onEvent(alert, 'closed.bs.alert', () => {
            if (!container.childElementCount) {
                hide(container);
            }
        });
    }
    container.append(alert);
    show(container);
    return alert;
}

async function getLoginConfig(): Promise<ILoginconfig> {
    // Always take the current server config. It used to be cached in localStorage without expiry,
    // which pinned returning browsers to whatever app registration / tenant / SSO domains they first
    // saw: after the registration moved to Text2Alpha, such a browser kept requesting tokens for
    // the retired Causality Link registration and every SSO login failed server-side validation.
    // The call is one small, unauthenticated GET per page load, so there is nothing worth caching.
    removeCacheKey(STORAGE_INFO.clLoginConfig.scope, STORAGE_INFO.clLoginConfig.key);
    return rootGetLoginConfig();
}

function runTimeMachine() {
    timeMachineModal.open();
}

function runStemUtil() {
    stemModal.open();
}

function runMetadataMonitorUtil() {
    metaMonitorModal.open();
}

function showPass(ssoMode: boolean, pass: HTMLInputElement, user: HTMLInputElement, accessToken: HTMLInputElement, username: HTMLInputElement, ssoDomains: string[]) {
    if (emailMatchesDomain(username.value, ssoDomains)) {
        pass.value = 'zvjadsfadsf';
        pass.classList.add('invisible');
        ssoMode = true;
    }
    else {
        if (ssoMode) {
            pass.value = '';
            user.value = '';
            accessToken.value = '';
        }
        pass.classList.remove('invisible');
        ssoMode = false;
    }
    return ssoMode;
}

async function updateImage() {
    const image = qs<HTMLImageElement>('a.navbar-brand > img');
    if (image.complete) {
        await fetch(image.src, { cache: 'reload', mode: 'no-cors' });
    }
    else {
        setTimeout(updateImage, 1000);
    }
}
